import { IconName } from '@lyse-as/formds-icons';
import { Component, Element, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import clsx from 'clsx';

import { FdsInputVariant } from '@/components';
import store from '@/store';

@Component({
  tag: 'fds-description',
  styleUrl: 'styles/description.scss',
  scoped: true,
})
export class Description {
  @Element() host: HTMLFdsDescriptionElement;

  /**
   * Sets the id attribute of the description
   */
  @Prop() descriptionId: string;

  /**
   * Add a description to the input. Is rendered underneath the input field
   */
  @Prop() description: string;

  /**
   * Sets the variant of input to use
   */
  @Prop() variant: FdsInputVariant = 'primary';

  /**
   * If true, description change appearance accordingly
   */
  @Prop() disabled = false;

  @State() iconName: IconName = 'info';

  @Watch('description')
  @Watch('variant')
  watchVariantChange() {
    switch (this.variant) {
      case 'primary': {
        this.iconName = 'info';
        break;
      }
      case 'success': {
        this.iconName = 'check_circle_filled';
        break;
      }
      case 'warning': {
        this.iconName = 'error_filled';
        break;
      }
      case 'error': {
        this.iconName = 'cancel_filled';
        break;
      }
      default: {
        console.warn('Unknown variant received');
        // fail safe
        this.iconName = 'info';
        break;
      }
    }
  }

  /**
   * Gets the height of the description text
   * @returns A number in pixels determining the height of the description
   */
  @Method()
  async getDescriptionHeight() {
    return this.host.getBoundingClientRect().height;
  }

  private isCustomDescriptionPresent = () => {
    const slottedElement = this.host.querySelector("[slot='custom-description']");

    if (slottedElement === null) {
      return false;
    }

    // "slottedElement instanceof HTMLSlotElement" fails in jest environment ...
    if (typeof slottedElement['assignedNodes'] === 'function') {
      return (slottedElement as HTMLSlotElement).assignedNodes().length > 0;
    }

    return !!slottedElement;
  };

  render() {
    const { variant, disabled, description, descriptionId, iconName } = this;

    const hasCustomDescription = this.isCustomDescriptionPresent();
    const hasDescription = !!description;
    const shouldDisplayDescriptionSection = hasDescription || hasCustomDescription;

    const containerClasses = clsx('fds-description', store.mode ?? '', {
      [`fds-description--${variant}`]: variant,
      'fds-description--disabled': disabled,
      'fds-description--hidden': !shouldDisplayDescriptionSection,
    });

    if (hasDescription && hasCustomDescription) {
      console.warn('Using description and custom description simultaneously is not supported.');
    }

    return (
      <Host class={containerClasses} id={descriptionId} aria-hidden={!shouldDisplayDescriptionSection}>
        <fds-icon name={iconName} size={16} aria-hidden="true" />
        {hasDescription && description}
        <slot name="custom-description" />
      </Host>
    );
  }
}
